import moment from 'moment';
import Swal from 'sweetalert2';
import HelperMixin from '../../Shared/HelperMixin.vue';
import { printDispatchDocument } from './printDispatchDocuments';

const SOURCE_MANUAL = 1;
const SOURCE_FROM_ORDER = 2;
const SOURCE_DIRECT = 3;
const STATUS_DRAFT = 1;

const SOURCE_OPTIONS = [
    { value: SOURCE_MANUAL, label: 'يدوي' },
    { value: SOURCE_FROM_ORDER, label: 'من طلب معتمد' },
    { value: SOURCE_DIRECT, label: 'مباشر (طوارئ)' },
];

export default {
    name: 'DispatchForm',
    mixins: [HelperMixin],

    async created() {
        await this.CheckLoginStatus();
        if (!this.canManage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'ليس لديك صلاحية إدارة الصرف');
            this.$router.push('/DispatchNew');
            return;
        }
        await Promise.all([this.loadBranches(), this.loadStockings()]);
        const id = this.$route.params.id;
        if (id) {
            this.isEditMode = true;
            await this.loadDispatch(Number(id));
        } else {
            this.isEditMode = false;
            const q = this.$route.query || {};
            if (q.sourceType) this.form.sourceType = Number(q.sourceType);
            if (q.orderId) {
                this.form.sourceType = SOURCE_FROM_ORDER;
                this.form.sourceMedicineOrderId = Number(q.orderId);
            }
            if (this.form.sourceType !== SOURCE_FROM_ORDER) {
                this.addEmptyLine();
            } else if (this.form.sourceMedicineOrderId) {
                await this.loadApprovedOrdersList();
                if (this.form.sourceStockingId) {
                    await this.loadOrderNationalPanels();
                }
            }
        }
    },

    watch: {
        'form.sourceStockingId'(val) {
            if (val) {
                this.refreshProductCatalog();
                if (this.form.sourceType === SOURCE_FROM_ORDER && this.form.sourceMedicineOrderId) {
                    this.loadOrderNationalPanels();
                }
            }
        },
        'form.facilityBranchesId'() {
            if (this.form.sourceType === SOURCE_FROM_ORDER) {
                this.approvedOrders = [];
            }
        },
        'form.sourceType'(val) {
            if (val !== SOURCE_FROM_ORDER) {
                this.orderNationalPanels = [];
                this.form.sourceMedicineOrderId = '';
                if (this.lines.length === 0) this.addEmptyLine();
            } else {
                this.lines = [];
            }
        },
        'form.sourceMedicineOrderId'(val) {
            if (this.form.sourceType === SOURCE_FROM_ORDER && val && this.form.sourceStockingId) {
                this.loadOrderNationalPanels();
            } else {
                this.orderNationalPanels = [];
            }
        },
    },

    computed: {
        canManage() {
            return this.hasInventoryAndStockAccessUi() || this.canDispenseFromMinistryUi();
        },
        isReadOnly() {
            return this.isEditMode && this.detail && Number(this.detail.status) !== STATUS_DRAFT;
        },
        pageTitle() {
            if (this.isEditMode && this.detail && this.detail.code) return `صرف — ${this.detail.code}`;
            return 'إنشاء صرف جديد';
        },
        sourceOptions() { return SOURCE_OPTIONS; },
        ministryStockings() {
            return (this.stockingsList || []).filter(s => !s.branchesId && s.status === 1);
        },
        facilityBranches() {
            return (this.branchesList || []).filter(b => b.status !== 9);
        },
        linkedOrderLabel() {
            if (!this.form.sourceMedicineOrderId) return '—';
            const o = this.approvedOrders.find(x => x.id === Number(this.form.sourceMedicineOrderId));
            if (o) return `${o.code || o.id} — ${o.branchName || ''}`;
            if (this.detail && this.detail.sourceMedicineOrderCode) {
                return `${this.detail.sourceMedicineOrderCode} (#${this.form.sourceMedicineOrderId})`;
            }
            return `#${this.form.sourceMedicineOrderId}`;
        },
        canPrintIssue() {
            return this.detail && this.detail.capabilities && this.detail.capabilities.canPrintIssueVoucher;
        },
        canPrintFinal() {
            return this.detail && this.detail.capabilities && this.detail.capabilities.canPrintFinalInvoice;
        },
        isOrderDispatchMode() {
            return this.form.sourceType === SOURCE_FROM_ORDER;
        },
        canLoadOrderPanels() {
            return this.isOrderDispatchMode
                && !this.isReadOnly
                && this.form.facilityBranchesId
                && this.form.sourceStockingId
                && this.form.sourceMedicineOrderId;
        },
        hasDispatchLines() {
            return this.lines.some(l => l.productsId && Number(l.quantity) > 0);
        },
    },

    data() {
        return {
            isEditMode: false,
            loading: false,
            saving: false,
            orderPanelsLoading: false,
            detail: null,
            branchesList: [],
            stockingsList: [],
            productCatalog: [],
            approvedOrders: [],
            orderNationalPanels: [],
            modalAttachments: [],
            form: {
                facilityBranchesId: '',
                sourceStockingId: '',
                sourceType: SOURCE_MANUAL,
                sourceMedicineOrderId: '',
                note: '',
            },
            lines: [],
        };
    },

    filters: {
        moment(d) { return d ? moment(d).format('DD/MM/YYYY') : '—'; },
        numberFormat(v) {
            const n = Number(v);
            return Number.isFinite(n) ? n.toLocaleString('en-US') : '—';
        },
    },

    methods: {
        extractError(err) {
            if (err && err.response && err.response.data) {
                return typeof err.response.data === 'string' ? err.response.data : (err.response.data.message || 'حدث خطأ غير متوقع');
            }
            return (err && err.message) || 'حدث خطأ غير متوقع';
        },

        async loadBranches() {
            try {
                const res = await this.$http.GetAllBranches();
                this.branchesList = (res && res.data && res.data.info) || [];
            } catch { this.branchesList = []; }
        },

        async loadStockings() {
            try {
                const res = await this.$http.GetAllStocking();
                const arr = (res && res.data && res.data.info) || (res && res.data) || [];
                this.stockingsList = Array.isArray(arr) ? arr : [];
            } catch { this.stockingsList = []; }
        },

        async refreshProductCatalog() {
            if (!this.form.sourceStockingId) {
                this.productCatalog = [];
                return;
            }
            try {
                const res = await this.$http.GetDispatchAvailableProducts(this.form.sourceStockingId, '');
                this.productCatalog = (res && res.data) || [];
            } catch { this.productCatalog = []; }
        },

        buildEmptyLine() {
            return {
                key: Date.now() + Math.random(),
                lineId: null,
                productsId: '',
                productsCountId: null,
                nationalFormularyItemId: null,
                tradeName: '',
                manufacturerName: '',
                packagingLabel: '',
                unitPrice: '',
                serialNumber: '',
                expiryDate: '',
                availableQty: 0,
                quantity: 0,
                batches: [],
                sourceMedicineOrderLineId: null,
            };
        },

        addEmptyLine() {
            this.lines.push(this.buildEmptyLine());
        },

        removeLine(idx) {
            const removed = this.lines[idx];
            this.lines.splice(idx, 1);
            if (this.lines.length === 0 && !this.isOrderDispatchMode) this.addEmptyLine();
            if (removed && removed.nationalFormularyItemId) {
                this.refreshRemainingForPanel(removed.nationalFormularyItemId);
            }
        },

        async loadDispatch(id) {
            this.loading = true;
            this.$blockUI.Start();
            try {
                const res = await this.$http.GetDispatchOne(id);
                this.detail = (res && res.data) || null;
                if (!this.detail) {
                    this.$helper.ShowMessage('error', 'خطأ', 'السند غير موجود');
                    this.$router.push('/DispatchNew');
                    return;
                }
                this.form.facilityBranchesId = this.detail.facilityBranchesId;
                this.form.sourceStockingId = this.detail.sourceStockingId;
                this.form.sourceType = this.detail.dispatchSourceType;
                this.form.sourceMedicineOrderId = this.detail.sourceMedicineOrderId || '';
                this.form.note = this.detail.note || '';
                await this.refreshProductCatalog();
                this.lines = (this.detail.lines || []).map(ln => ({
                    key: ln.id,
                    lineId: ln.id,
                    productsId: ln.productsId,
                    productsCountId: null,
                    nationalFormularyItemId: null,
                    tradeName: ln.productName || '',
                    manufacturerName: '',
                    packagingLabel: '',
                    unitPrice: ln.unitPrice,
                    serialNumber: ln.serialNumber || '',
                    expiryDate: ln.expiryDate ? moment(ln.expiryDate).format('YYYY-MM-DD') : '',
                    availableQty: 0,
                    quantity: ln.quantity,
                    batches: [],
                    sourceMedicineOrderLineId: ln.sourceMedicineOrderLineId,
                }));
                if (this.lines.length === 0 && !this.isOrderDispatchMode) this.addEmptyLine();
                if (!this.isOrderDispatchMode) {
                    for (const ln of this.lines) {
                        if (ln.productsId) await this.loadBatchesForLine(ln);
                    }
                } else if (this.form.sourceMedicineOrderId) {
                    await this.loadApprovedOrdersList();
                    await this.loadOrderNationalPanels();
                }
            } catch (err) {
                this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                this.$router.push('/DispatchNew');
            } finally {
                this.loading = false;
                this.$blockUI.Stop();
            }
        },

        async onProductSelected(line) {
            const prod = this.productCatalog.find(p => p.id === Number(line.productsId));
            if (!prod) return;
            line.tradeName = prod.tradeName;
            line.manufacturerName = prod.manufacturerName || '';
            line.unitPrice = prod.unitPrice != null ? prod.unitPrice : '';
            line.availableQty = prod.availableQuantity || 0;
            line.serialNumber = '';
            line.expiryDate = '';
            line.batches = [];
            try {
                const pkgRes = await this.$http.GetProductWithPackagings(prod.id);
                const pkgs = (pkgRes && pkgRes.data && pkgRes.data.packagings) || [];
                line.packagingLabel = pkgs.length
                    ? pkgs.map(p => `${p.unitName || ''}${p.quantity > 0 ? ' × ' + p.quantity : ''}`).join(' / ')
                    : (prod.packagingLabel || '—');
            } catch {
                line.packagingLabel = prod.packagingLabel || '—';
            }
            await this.loadBatchesForLine(line);
        },

        async loadBatchesForLine(line) {
            if (!line.productsId || !this.form.sourceStockingId) return;
            try {
                const res = await this.$http.GetDispatchAvailableBatches(line.productsId, this.form.sourceStockingId);
                line.batches = (res && res.data) || [];
            } catch { line.batches = []; }
        },

        onBatchSelected(line) {
            const batch = (line.batches || []).find(b => b.serialNumber === line.serialNumber);
            if (batch) {
                line.expiryDate = batch.expiryDate ? moment(batch.expiryDate).format('YYYY-MM-DD') : '';
                line.availableQty = batch.availableQuantity || 0;
                if (batch.price != null) line.unitPrice = batch.price;
            }
        },

        async loadApprovedOrdersList() {
            if (!this.form.facilityBranchesId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'اختر الجهة المستفيدة أولاً');
                return;
            }
            try {
                const res = await this.$http.GetDispatchApprovedOrders(this.form.facilityBranchesId);
                this.approvedOrders = (res && res.data) || [];
            } catch { this.approvedOrders = []; }
        },

        async onOrderSelected() {
            await this.loadApprovedOrdersList();
            if (this.canLoadOrderPanels) {
                await this.loadOrderNationalPanels();
            }
        },

        async loadOrderNationalPanels() {
            if (!this.canLoadOrderPanels) {
                if (!this.form.facilityBranchesId || !this.form.sourceStockingId) {
                    this.$helper.ShowMessage('warning', 'تنبيه', 'اختر الجهة المستفيدة والمخزن المصدر أولاً');
                } else if (!this.form.sourceMedicineOrderId) {
                    this.$helper.ShowMessage('warning', 'تنبيه', 'اختر طلباً معتمداً');
                }
                return;
            }

            this.orderPanelsLoading = true;
            this.$blockUI.Start();
            try {
                const res = await this.$http.GetDispatchOrderItems(this.form.sourceMedicineOrderId);
                const data = (res && res.data) || {};
                const items = data.items || data.Items || [];

                this.orderNationalPanels = await Promise.all(items.map(async (it) => {
                    const nationalId = it.nationalFormularyItemId;
                    const panel = {
                        nationalFormularyItemId: nationalId,
                        scientificName: it.scientificName,
                        nationalCode: it.nationalCode,
                        quantityApproved: it.quantityApproved,
                        quantityDispensed: 0,
                        remainingQuantity: it.quantityApproved,
                        primaryOrderLineId: it.primaryOrderLineId,
                        batchRows: [],
                        loading: true,
                    };
                    await Promise.all([
                        this.loadBatchesForPanel(panel),
                        this.refreshRemainingForPanel(panel),
                    ]);
                    panel.loading = false;
                    return panel;
                }));
            } catch (err) {
                this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                this.orderNationalPanels = [];
            } finally {
                this.orderPanelsLoading = false;
                this.$blockUI.Stop();
            }
        },

        async loadBatchesForPanel(panel) {
            try {
                const res = await this.$http.GetDispatchProductBatchesByNationalItem(
                    panel.nationalFormularyItemId,
                    this.form.sourceStockingId,
                );
                const rows = (res && res.data) || [];
                panel.batchRows = rows.map(b => ({
                    productsCountId: b.productsCountId || b.id,
                    productsId: b.productsId,
                    tradeName: b.tradeName,
                    manufacturerName: b.manufacturerName || '',
                    packagingLabel: b.packagingLabel || '—',
                    serialNumber: b.serialNumber || b.batchNumber || '',
                    expiryDate: b.expiryDate ? moment(b.expiryDate).format('YYYY-MM-DD') : '',
                    availableQuantity: b.availableQuantity || 0,
                    unitPrice: b.unitPrice != null ? b.unitPrice : (b.price != null ? b.price : ''),
                    requestedQty: 0,
                    adding: false,
                }));
            } catch {
                panel.batchRows = [];
            }
        },

        async refreshRemainingForPanel(panelOrNationalId) {
            const panel = typeof panelOrNationalId === 'object'
                ? panelOrNationalId
                : this.orderNationalPanels.find(p => p.nationalFormularyItemId === panelOrNationalId);
            if (!panel || !this.form.sourceMedicineOrderId) return;

            try {
                const res = await this.$http.GetDispatchOrderRemainingQuantity(
                    this.form.sourceMedicineOrderId,
                    panel.nationalFormularyItemId,
                );
                const data = (res && res.data) || {};
                panel.quantityApproved = data.quantityApproved ?? panel.quantityApproved;
                panel.quantityDispensed = data.quantityDispensed ?? 0;
                const serverRemaining = data.remainingQuantity ?? 0;
                const localPending = this.getLocalQtyForNationalItem(panel.nationalFormularyItemId);
                panel.remainingQuantity = Math.max(0, serverRemaining - localPending);
            } catch { /* ignore */ }
        },

        getLocalQtyForNationalItem(nationalItemId) {
            return this.lines
                .filter(l => l.nationalFormularyItemId === nationalItemId
                    && Number(l.quantity) > 0
                    && !l.lineId)
                .reduce((sum, l) => sum + Number(l.quantity), 0);
        },

        getEffectiveRemaining(panel) {
            return Math.max(0, panel.remainingQuantity ?? 0);
        },

        maxBatchRequestQty(panel, batchRow) {
            const rem = this.getEffectiveRemaining(panel);
            const avail = Number(batchRow.availableQuantity) || 0;
            return Math.min(rem, avail);
        },

        async addBatchToDispatch(panel, batchRow) {
            const qty = Number(batchRow.requestedQty);
            if (!qty || qty <= 0) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'أدخل كمية مطلوبة أكبر من صفر');
                return;
            }
            const maxQty = this.maxBatchRequestQty(panel, batchRow);
            if (qty > maxQty) {
                this.$helper.ShowMessage(
                    'warning',
                    'تنبيه',
                    `الكمية تتجاوز الحد المسموح (${maxQty}) — المتبقي من الطلب أو المتوفر للتشغيلة`,
                );
                return;
            }

            batchRow.adding = true;
            try {
                const body = {
                    OrderId: Number(this.form.sourceMedicineOrderId),
                    NationalFormularyItemId: panel.nationalFormularyItemId,
                    ProductsCountId: batchRow.productsCountId,
                    Quantity: qty,
                    SourceMedicineOrderLineId: panel.primaryOrderLineId,
                };
                if (this.isEditMode && this.detail && this.detail.id) {
                    body.DispatchId = this.detail.id;
                }

                const res = await this.$http.DispatchAddBatchAllocation(body);
                const data = (res && res.data) || {};

                const newLine = {
                    key: Date.now() + Math.random(),
                    lineId: data.id || null,
                    productsId: batchRow.productsId,
                    productsCountId: batchRow.productsCountId,
                    nationalFormularyItemId: panel.nationalFormularyItemId,
                    tradeName: batchRow.tradeName,
                    manufacturerName: batchRow.manufacturerName,
                    packagingLabel: batchRow.packagingLabel,
                    unitPrice: batchRow.unitPrice,
                    serialNumber: batchRow.serialNumber,
                    expiryDate: batchRow.expiryDate,
                    availableQty: batchRow.availableQuantity,
                    quantity: qty,
                    batches: [],
                    sourceMedicineOrderLineId: panel.primaryOrderLineId,
                };

                if (!body.DispatchId) {
                    this.lines.push(newLine);
                } else {
                    await this.loadDispatch(this.detail.id);
                }

                batchRow.requestedQty = 0;
                await this.refreshRemainingForPanel(panel);
                this.$helper.ShowMessage('success', 'نجاح', `تمت إضافة ${qty} قطعة من التشغيلة ${batchRow.serialNumber || '—'}`);
            } catch (err) {
                this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
            } finally {
                batchRow.adding = false;
            }
        },

        async addAllBatchesForPanel(panel) {
            let added = 0;
            for (const batchRow of panel.batchRows) {
                const maxQty = this.maxBatchRequestQty(panel, batchRow);
                if (maxQty <= 0) continue;
                batchRow.requestedQty = maxQty;
                await this.addBatchToDispatch(panel, batchRow);
                added += 1;
            }
            if (added === 0) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'لا توجد كميات متاحة للإضافة');
            }
        },

        OnAttachmentChange(event) {
            const files = (event && event.target && event.target.files) || [];
            for (let i = 0; i < files.length; i++) {
                const f = files[i];
                const r = new FileReader();
                r.onload = () => {
                    this.modalAttachments.push({ FileName: f.name, ContentType: f.type, Base64Content: r.result });
                };
                r.readAsDataURL(f);
            }
        },

        validateForm() {
            if (!this.form.facilityBranchesId || !this.form.sourceStockingId) {
                return 'الرجاء اختيار الجهة المستفيدة والمخزن المصدر';
            }
            if (this.form.sourceType === SOURCE_FROM_ORDER && !this.form.sourceMedicineOrderId) {
                return 'الرجاء ربط طلب معتمد';
            }
            if (this.form.sourceType === SOURCE_DIRECT && !this.modalAttachments.length && !this.isEditMode) {
                return 'الصرف المباشر يتطلب رفع المستندات التعزيزية';
            }
            const activeLines = this.lines.filter(l => l.productsId && Number(l.quantity) > 0);
            if (activeLines.length === 0) return 'الرجاء إضافة صنف واحد على الأقل';
            for (const ln of activeLines) {
                if (Number(ln.quantity) > Number(ln.availableQty || 0)) {
                    return `الكمية المطلوبة تتجاوز المتوفر للصنف "${ln.tradeName}"`;
                }
            }
            return null;
        },

        buildLinesPayload() {
            return this.lines
                .filter(l => l.productsId && Number(l.quantity) > 0)
                .map(ln => ({
                    ProductsId: Number(ln.productsId),
                    Quantity: Number(ln.quantity),
                    UnitPrice: ln.unitPrice === '' || ln.unitPrice == null ? null : Number(ln.unitPrice),
                    SerialNumber: ln.serialNumber || null,
                    ExpiryDate: ln.expiryDate || null,
                    SourceMedicineOrderLineId: ln.sourceMedicineOrderLineId || null,
                }));
        },

        async saveDispatch() {
            const err = this.validateForm();
            if (err) {
                this.$helper.ShowMessage('warning', 'تنبيه', err);
                return;
            }
            this.saving = true;
            this.$blockUI.Start();
            try {
                if (this.isEditMode && this.detail && Number(this.detail.status) === STATUS_DRAFT) {
                    await this.syncLinesOnEdit();
                    this.$helper.ShowMessage('success', 'نجاح', 'تم حفظ التعديلات');
                    await this.loadDispatch(this.detail.id);
                } else if (!this.isEditMode) {
                    const payload = {
                        SourceStockingId: Number(this.form.sourceStockingId),
                        FacilityBranchesId: Number(this.form.facilityBranchesId),
                        DispatchSourceType: Number(this.form.sourceType),
                        SourceMedicineOrderId: this.form.sourceMedicineOrderId ? Number(this.form.sourceMedicineOrderId) : null,
                        Note: this.form.note || '',
                        Lines: this.buildLinesPayload(),
                        Attachments: this.form.sourceType === SOURCE_DIRECT ? this.modalAttachments : null,
                    };
                    const res = await this.$http.DispatchCreate(payload);
                    const id = res && res.data && res.data.id;
                    this.$helper.ShowMessage('success', 'نجاح', 'تم إنشاء الصرف');
                    if (id) this.$router.replace({ name: 'DispatchNewEdit', params: { id } });
                    else this.$router.push('/DispatchNew');
                }
            } catch (e) {
                this.$helper.ShowMessage('error', 'خطأ', this.extractError(e));
            } finally {
                this.saving = false;
                this.$blockUI.Stop();
            }
        },

        async syncLinesOnEdit() {
            const dispatchId = this.detail.id;
            const serverLines = (this.detail.lines || []).map(l => l.id);
            const localWithId = this.lines.filter(l => l.lineId && l.productsId && Number(l.quantity) > 0);
            const localIds = localWithId.map(l => l.lineId);
            for (const sid of serverLines) {
                if (!localIds.includes(sid)) {
                    await this.$http.DispatchRemoveLine({ DispatchId: dispatchId, LineId: sid });
                }
            }
            for (const ln of this.lines) {
                if (!ln.productsId || Number(ln.quantity) <= 0) continue;
                const body = {
                    DispatchId: dispatchId,
                    LineId: ln.lineId,
                    ProductsId: Number(ln.productsId),
                    Quantity: Number(ln.quantity),
                    UnitPrice: ln.unitPrice === '' || ln.unitPrice == null ? null : Number(ln.unitPrice),
                    SerialNumber: ln.serialNumber || null,
                    ExpiryDate: ln.expiryDate || null,
                    SourceMedicineOrderLineId: ln.sourceMedicineOrderLineId || null,
                };
                if (ln.lineId) await this.$http.DispatchUpdateLine(body);
                else await this.$http.DispatchAddLine(body);
            }
        },

        goBack() {
            this.$router.push('/DispatchNew');
        },

        printIssue() {
            if (!this.detail) return;
            Swal.fire({
                title: 'تأكيد طباعة سند الصرف',
                text: 'سيتم خصم الكميات من المخزن المصدر.',
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-primary me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد وطباعة',
                cancelButtonText: 'إلغاء',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchPrintIssueVoucher(this.detail.id)
                    .then(async () => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم خصم الكميات');
                        await this.loadDispatch(this.detail.id);
                        printDispatchDocument(this.detail, 'issue', { logoUrl: this.logoPath });
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },

        printFinal() {
            if (!this.detail) return;
            Swal.fire({
                title: 'تأكيد طباعة الفاتورة النهائية',
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-primary me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد وطباعة',
                cancelButtonText: 'إلغاء',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchPrintFinalInvoice(this.detail.id)
                    .then(async () => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم طباعة الفاتورة النهائية');
                        await this.loadDispatch(this.detail.id);
                        printDispatchDocument(this.detail, 'finalInvoice', { logoUrl: this.logoPath });
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },
    },
};
